import { AnimationEvent } from '@angular/animations';
import { AriaLivePoliteness } from '@angular/cdk/a11y';
import { Platform } from '@angular/cdk/platform';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, DomPortal, TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Observable, Subject, take } from 'rxjs';

import { ndtSnackBarAnimations } from './snack-bar-animations';
import { NdtSnackBarConfig } from './snack-bar-config';

let uniqueId = 0;

/**
 * Base class for snack bar containers.
 * @docs-private
 */
@Directive()
export abstract class NdtSnackBarContainerBase extends BasePortalOutlet implements OnDestroy {
  private document = inject(DOCUMENT);
  private trackedModals = new Set<Element>();

  /** The number of milliseconds to wait before announcing the snack bar's content. */
  private readonly announceDelay: number = 150;

  /** The timeout for announcing the snack bar's content. */
  private announceTimeoutId: number;

  /** Whether the component has been destroyed. */
  private destroyed = false;

  /** The portal outlet inside of this container into which the snack bar content will be loaded. */
  @ViewChild(CdkPortalOutlet, { static: true }) portalOutlet: CdkPortalOutlet;

  /** Subject for notifying that the snack bar has announced to screen readers. */
  readonly onAnnounce: Subject<void> = new Subject();

  /** Subject for notifying that the snack bar has exited from view. */
  readonly _exit: Subject<void> = new Subject();

  /** Subject for notifying that the snack bar has finished entering the view. */
  readonly onEnter: Subject<void> = new Subject();

  /** Subject for notifying that the snack bar has exited from view. */
  readonly mouseEnter: Subject<void> = new Subject();

  /** Subject for notifying that the snack bar has finished entering the view. */
  readonly mouseOut: Subject<void> = new Subject();

  /** The state of the snack bar animations. */
  animationState = 'void';

  /** aria-live value for the live region. */
  live: AriaLivePoliteness;

  /**
   * Role of the live region. This is only for Firefox as there is a known issue where Firefox +
   * JAWS does not read out aria-live message.
   */
  role?: 'status' | 'alert';

  /** Unique ID of the aria-live element. */
  readonly liveElementId = `ndt-snack-bar-container-live-${uniqueId++}`;

  constructor(
    private ngZone: NgZone,
    protected elementRef: ElementRef<HTMLElement>,
    private changeDetectorRef: ChangeDetectorRef,
    private platform: Platform,
    /** The snack bar configuration. */
    public snackBarConfig: NdtSnackBarConfig,
  ) {
    super();

    // Use aria-live rather than a live role like 'alert' or 'status'
    // because NVDA and JAWS have show inconsistent behavior with live roles.
    if (snackBarConfig.politeness === 'assertive' && !snackBarConfig.announcementMessage) {
      this.live = 'assertive';
    } else if (snackBarConfig.politeness === 'off') {
      this.live = 'off';
    } else {
      this.live = 'polite';
    }

    // Only set role for Firefox. Set role based on aria-live because setting role="alert" implies
    // aria-live="assertive" which may cause issues if aria-live is set to "polite" above.
    if (this.platform.FIREFOX) {
      if (this.live === 'polite') {
        this.role = 'status';
      }
      if (this.live === 'assertive') {
        this.role = 'alert';
      }
    }
  }

  /**
   * Attach a component portal as content to this snack bar container.
   * @param portal
   */
  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    const result = this.portalOutlet.attachComponentPortal(portal);
    this.afterPortalAttached();
    return result;
  }

  /**
   * Attach a template portal as content to this snack bar container.
   * @param portal
   */
  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    const result = this.portalOutlet.attachTemplatePortal(portal);
    this.afterPortalAttached();
    return result;
  }

  /**
   * Attaches a DOM portal to the snack bar container.
   * @param portal
   * @deprecated To be turned into a method.
   * @breaking-change 10.0.0
   */
  override attachDomPortal = (portal: DomPortal) => {
    const result = this.portalOutlet.attachDomPortal(portal);
    this.afterPortalAttached();
    return result;
  };

  /**
   * Handle end of animations, updating the state of the snackbar.
   * @param event
   */
  onAnimationEnd(event: AnimationEvent) {
    const { fromState, toState } = event;

    if ((toState === 'void' && fromState !== 'void') || toState === 'hidden') {
      this.completeExit();
    }

    if (toState === 'visible') {
      // Note: we shouldn't use `this` inside the zone callback,
      // because it can cause a memory leak.
      const { onEnter } = this;

      this.ngZone.run(() => {
        onEnter.next();
        onEnter.complete();
      });
    }
  }

  /** Begin animation of snack bar entrance into view. */
  enter(): void {
    if (!this.destroyed) {
      this.animationState = 'visible';
      this.changeDetectorRef.detectChanges();
      this.screenReaderAnnounce();
    }
  }

  /** Begin animation of the snack bar exiting from view. */
  exit(): Observable<void> {
    // It's common for snack bars to be opened by random outside calls like HTTP requests or
    // errors. Run inside the NgZone to ensure that it functions correctly.
    this.ngZone.run(() => {
      // Note: this one transitions to `hidden`, rather than `void`, in order to handle the case
      // where multiple snack bars are opened in quick succession (e.g. two consecutive calls to
      // `NdtSnackBar.open`).
      this.animationState = 'hidden';

      // Mark this element with an 'exit' attribute to indicate that the snackbar has
      // been dismissed and will soon be removed from the DOM. This is used by the snackbar
      // test harness.
      this.elementRef.nativeElement.setAttribute('ndt-exit', '');

      // If the snack bar hasn't been announced by the time it exits it wouldn't have been open
      // long enough to visually read it either, so clear the timeout for announcing.
      clearTimeout(this.announceTimeoutId);
    });

    return this._exit;
  }

  /** Makes sure the exit callbacks have been invoked when the element is destroyed. */
  ngOnDestroy() {
    this.destroyed = true;
    this.clearFromModals();
    this.completeExit();
  }

  /**
   * Called after the portal contents have been attached. Can be
   * used to modify the DOM once it's guaranteed to be in place.
   */
  protected afterPortalAttached() {
    const element: HTMLElement = this.elementRef.nativeElement;
    const panelClasses = this.snackBarConfig.panelClass;

    if (panelClasses) {
      if (Array.isArray(panelClasses)) {
        panelClasses.forEach((cssClass) => element.classList.add(cssClass));
      } else {
        element.classList.add(panelClasses);
      }
    }

    this.exposeToModals();
  }

  /**
   * Waits for the zone to settle before removing the element. Helps prevent
   * errors where we end up removing an element which is in the middle of an animation.
   */
  private completeExit() {
    this.ngZone.onMicrotaskEmpty.pipe(take(1)).subscribe(() => {
      this.ngZone.run(() => {
        this._exit.next();
        this._exit.complete();
      });
    });
  }

  /**
   * Some browsers won't expose the accessibility node of the live element if there is an
   * `aria-modal` and the live element is outside of it. This method works around the issue by
   * pointing the `aria-owns` of all modals to the live element.
   */
  private exposeToModals() {
    // Note that the selector here is limited to CDK overlays at the moment in order to reduce the
    // section of the DOM we need to look through. This should cover all the cases we support, but
    // the selector can be expanded if it turns out to be too narrow.
    const id = this.liveElementId;
    const modals = this.document.querySelectorAll('body > .cdk-overlay-container [aria-modal="true"]');

    for (let i = 0; i < modals.length; i++) {
      const modal = modals[i];
      const ariaOwns = modal.getAttribute('aria-owns');
      this.trackedModals.add(modal);

      if (!ariaOwns) {
        modal.setAttribute('aria-owns', id);
      } else if (ariaOwns.indexOf(id) === -1) {
        modal.setAttribute('aria-owns', `${ariaOwns} ${id}`);
      }
    }
  }

  /** Clears the references to the live element from any modals it was added to. */
  private clearFromModals() {
    this.trackedModals.forEach((modal) => {
      const ariaOwns = modal.getAttribute('aria-owns');

      if (ariaOwns) {
        const newValue = ariaOwns.replace(this.liveElementId, '').trim();

        if (newValue.length > 0) {
          modal.setAttribute('aria-owns', newValue);
        } else {
          modal.removeAttribute('aria-owns');
        }
      }
    });
    this.trackedModals.clear();
  }

  /**
   * Starts a timeout to move the snack bar content to the live region so screen readers will
   * announce it.
   */
  private screenReaderAnnounce() {
    if (!this.announceTimeoutId) {
      this.ngZone.runOutsideAngular(() => {
        this.announceTimeoutId = setTimeout(() => {
          const inertElement = this.elementRef.nativeElement.querySelector('[aria-hidden]');
          const liveElement = this.elementRef.nativeElement.querySelector('[aria-live]');

          if (inertElement && liveElement) {
            // If an element in the snack bar content is focused before being moved
            // track it and restore focus after moving to the live region.
            let focusedElement: HTMLElement | null = null;
            if (
              this.platform.isBrowser &&
              document.activeElement instanceof HTMLElement &&
              inertElement.contains(document.activeElement)
            ) {
              focusedElement = document.activeElement;
            }

            inertElement.removeAttribute('aria-hidden');
            liveElement.appendChild(inertElement);
            focusedElement?.focus();

            this.onAnnounce.next();
            this.onAnnounce.complete();
          }
        }, this.announceDelay);
      });
    }
  }
}

/**
 * Internal component that wraps user-provided snack bar content.
 * @docs-private
 */
@Component({
  selector: 'ndt-snack-bar-container',
  templateUrl: 'snack-bar-container.html',
  // In Ivy embedded views will be change detected from their declaration place, rather than
  // where they were stamped out. This means that we can't have the snack bar container be OnPush,
  // because it might cause snack bars that were opened from a template not to be out of date.
  // tslint:disable-next-line:validate-decorators
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  animations: [ndtSnackBarAnimations.snackBarState],
  host: {
    class: 'mdc-snackbar ndt-mdc-snack-bar-container mdc-snackbar--open',
    '[@state]': 'animationState',
    '(@state.done)': 'onAnimationEnd($event)',
  },
})
export class NdtSnackBarContainer extends NdtSnackBarContainerBase {
  /**
   * Element that will have the `mdc-snackbar__label` class applied if the attached component
   * or template does not have it. This ensures that the appropriate structure, typography, and
   * color is applied to the attached view.
   */
  @ViewChild('label', { static: true }) label: ElementRef;

  @HostListener('mouseenter') onMouseEnter(): void {
    this.mouseEnter.next();
  }

  @HostListener('mouseleave') onMouseLeave(): void {
    this.mouseOut.next();
  }

  /** Applies the correct CSS class to the label based on its content. */
  protected override afterPortalAttached() {
    super.afterPortalAttached();

    // Check to see if the attached component or template uses the MDC template structure,
    // specifically the MDC label. If not, the container should apply the MDC label class to this
    // component's label container, which will apply MDC's label styles to the attached view.
    const label = this.label.nativeElement;
    const labelClass = 'mdc-snackbar__label';
    label.classList.toggle(labelClass, !label.querySelector(`.${labelClass}`));
  }
}
